import type { PointsScopeEvent } from '@brush/api-client';
import { BuilderSection, Labelled, Worked, controlClass } from '@brush/ui';
import {
  describeLocksAt,
  firstEventDate,
  formatInstant,
  startedEvents,
} from '../../fantasy/contest';

/**
 * Section 5 — when entry closes.
 *
 * §6.1 in one control and three sentences. `LocksAt` defaults to the start of the earliest event
 * the leaderboard scores, <b>may be brought earlier and never pushed later</b>, and a contest whose
 * scope already contains a started event is refused outright rather than warned about — entrants
 * would be drafting with results in hand, which is not a fairness nicety but the whole game.
 *
 * <b>Left empty is the good answer, and the control says so.</b> The server computes the default
 * from the events it has just resolved, which is a better number than anything this screen can
 * work out — see the timezone note below. So the box is optional and its placeholder is the rule,
 * rather than being pre-filled with a value the operator then has to recognise as a default.
 *
 * <b>The timezone gap is disclosed, not hidden.</b> A tournament carries a date and a wall-clock
 * time with no offset modelled anywhere, so the server reads that pair as UTC — a stated limitation
 * with no honest alternative until a room carries a timezone. This box, being a browser control, is
 * in the operator's own zone. Where the two could disagree the screen says which is which rather
 * than quietly converting, because a lock time that silently moved by hours is the one error here
 * nobody would catch.
 */
export function ContestLockSection({
  step,
  locksAt,
  onLocksAtChange,
  events,
  eventsKnown,
  storedLocksAt,
  readOnly,
  todayUtc,
}: {
  step: number;
  /** `yyyy-mm-ddThh:mm` for the box, or empty for "use the rule". */
  locksAt: string;
  onLocksAtChange: (next: string) => void;
  /** The events the leaderboard resolves to, for the default and for §6.1's refusal. */
  events: readonly PointsScopeEvent[];
  /** Whether the resolution has landed. Distinguishes "no events" from "not counted yet". */
  eventsKnown: boolean;
  /** What the contest actually locked at, once it has published. */
  storedLocksAt: string | null;
  readOnly: boolean;
  /**
   * The <em>UTC</em> date, `yyyy-mm-dd`, passed in so this stays a pure render of somebody else's
   * clock — and shared with section 6's publish blocker, which asks the same question strictly.
   * The frame is not decoration: see `certainlyStartedEvents`.
   */
  todayUtc: string;
}) {
  const first = firstEventDate(events);
  // `startedEvents`, not its strict sibling: this is a warning, and one that is eager by a day is
  // worth having where one that refuses the publish by a day is not.
  const started = startedEvents(events, todayUtc);

  if (readOnly) {
    return (
      <BuilderSection step={step} title="Lock timing" summary="Set when this contest published.">
        <p className="text-sm text-ink-950">
          Entry closes {formatInstant(storedLocksAt)}.
        </p>
        <p className="mt-1.5 text-[11.5px] leading-relaxed text-ink-800/60">
          Entrants were told this time. It cannot move — and it does not need to, since an operator
          can still close entry early from the top of this screen if the series is brought forward.
        </p>
      </BuilderSection>
    );
  }

  return (
    <BuilderSection
      step={step}
      title="Lock timing"
      summary={describeLocksAt(
        locksAt,
        first ? `When the first event starts — ${first}.` : 'When the first event starts.',
      )}
    >
      <div className="space-y-3">
        <Labelled
          label="Close entry at"
          hint="Leave empty and it closes when the first event in the series starts, which is what you almost always want. Set a time to close earlier — never later."
        >
          <input
            type="datetime-local"
            value={locksAt}
            onChange={(e) => onLocksAtChange(e.target.value)}
            className={`num ${controlClass}`}
          />
        </Labelled>

        {locksAt !== '' ? (
          <button
            type="button"
            onClick={() => onLocksAtChange('')}
            className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-800/55 transition hover:text-ink-950"
          >
            Back to when the first event starts
          </button>
        ) : null}

        <Worked>
          Entry has to close before anybody plays. Once one event has run, its results are public —
          and an entrant picking a team afterwards would be picking players they already know
          cashed. That is why this can be brought forward and never pushed back.
        </Worked>

        {/* §6.1's refusal, stated where it can still be fixed cheaply rather than at the publish
            confirmation. The comparison is by date, because that is all the scope resolution
            reports — the server compares the full instant and is the control, which is why this
            says "will refuse" rather than reporting a verdict of its own. */}
        {started.length > 0 ? (
          <div className="border border-line border-l-4 border-l-bad bg-surface p-3">
            <p className="text-[12px] font-medium text-ink-950">
              {started.length === 1
                ? 'One event in this series has already run.'
                : `${started.length} events in this series have already run.`}
            </p>
            <p className="mt-1 text-[11.5px] leading-relaxed text-ink-800/70">
              Publishing will be refused: entrants would be drafting with these results in front of
              them. Narrow the leaderboard’s scope in section 1, or run this contest over a series
              that has not started.
            </p>
            <ul className="num mt-2 space-y-0.5 text-[11.5px] text-ink-800/70">
              {started.slice(0, 10).map((event) => (
                <li key={event.id}>
                  {event.internalId} · {event.name} · {event.date}
                </li>
              ))}
            </ul>
            {started.length > 10 ? (
              <p className="mt-1 text-[11px] text-ink-800/50">
                …and {started.length - 10} more.
              </p>
            ) : null}
          </div>
        ) : null}

        {eventsKnown && events.length > 0 && started.length === 0 && first !== null ? (
          <p className="text-[11.5px] leading-relaxed text-ink-800/60">
            The first of the {events.length} {events.length === 1 ? 'event' : 'events'} this
            contest scores is on {first}. Times below the day are read from the tournament’s own
            start time; the box above is in your browser’s timezone, so if you set one within a few
            hours of that first event, check it against the schedule.
          </p>
        ) : null}
      </div>
    </BuilderSection>
  );
}
